import { DOM, Helpers } from '@front/utils/index';
import { createComponent } from '@front/utils/alpine';
import { throttle } from 'lodash-es';
const BaseSlider = createComponent((opts) => ({
    initIndex: opts.initIndex ?? 0,
    initPage: opts.initIndex ?? 0,
    keyboard: opts.keyboard ?? false,
    focus: opts.focus ?? false,
    loop: opts.loop ?? false,
    _perPage: opts.perPage ?? 1,
    autoplay: opts.autoplay ?? false,
    autoplayInterval: opts.autoplayInterval ?? 5000,
    timer: null,
    pages: 0,
    scrollLeft: 0,
    offsetWidth: 0,
    scrollWidth: 0,
    get currentPage() {
        return this.noNext
            ? this.pages - 1
            : Math.floor(this.currentIndex / this.itemsPerPage);
    },
    get currentIndex() {
        const scrollEl = Array.from(this.$refs.wrapper.querySelectorAll(':scope > *')).find((el) => el.offsetLeft >= this.scrollLeft &&
            this.scrollLeft < el.offsetLeft + el.offsetWidth);
        return DOM.getChildIndex(this.$refs.wrapper, scrollEl);
    },
    get itemsPerPage() {
        if (typeof this._perPage === 'number') {
            return this._perPage;
        }
        const [, perPage = 1] = Object.entries(this._perPage).findLast(([key]) => Helpers.Screen.isWiderThanBreakpoint(key)) ?? [];
        return perPage;
    },
    get totalItems() {
        return DOM.getChildCount(this.$refs.wrapper);
    },
    get isStart() {
        return this.scrollLeft == 0;
    },
    get isEnd() {
        return this.scrollWidth - this.scrollLeft - this.offsetWidth <= 0;
    },
    get noPrev() {
        return this.loop ? false : this.isStart;
    },
    get noNext() {
        return this.loop ? false : this.isEnd;
    },
    prev(navType = 'page', behavior = 'smooth') {
        let index = this.noNext ? this.currentIndex : this.currentPage;
        if (this.loop && this.isStart) {
            index = navType == 'page' ? this.pages : this.totalItems;
        }
        this.goTo(index - 1, navType, behavior);
    },
    next(navType = 'page', behavior = 'smooth') {
        let index = this.currentPage + 1;
        if (this.loop && this.isEnd) {
            index = 0;
        }
        this.goTo(index, navType, behavior);
    },
    scrollTo(index, navType = 'page') {
        this.goTo(index, navType, 'smooth');
        this.resetAutoplayTimer();
    },
    goTo(index, navType = 'page', behavior = 'instant') {
        const toEl = DOM.getChildAt(this.$refs.wrapper, index * (navType == 'page' ? this.itemsPerPage : 1));
        this.$refs.wrapper?.scrollTo({
            left: toEl?.offsetLeft ?? 0,
            behavior,
        });
    },
    handleKeyboardEvent(event) {
        switch (event.key) {
            case 'ArrowLeft':
                this.prev();
                break;
            case 'ArrowRight':
                this.next();
                break;
        }
        this.resetAutoplayTimer();
    },
    handleScrollEvent() {
        const { scrollLeft, offsetWidth, scrollWidth } = this.$refs.wrapper;
        this.scrollLeft = Math.floor(scrollLeft);
        this.offsetWidth = offsetWidth;
        this.scrollWidth = scrollWidth;
    },
    handleResizeEvent() {
        try {
            const childenCount = this.$refs.wrapper.childElementCount ?? 0;
            this.pages = Math.ceil(childenCount / this.itemsPerPage);
            this.handleScrollEvent();
        }
        catch {
            //
        }
    },
    pauseAutoplay() {
        clearInterval(this.timer ? this.timer : undefined);
    },
    resetAutoplayTimer() {
        this.pauseAutoplay();
        if (!this.autoplay)
            return;
        this.timer = setInterval(() => {
            this.next('page');
        }, this.autoplayInterval);
    },
    async start() {
        await this.$nextTick();
        this.handleScrollEvent();
        this.$refs.wrapper.addEventListener('scroll', throttle(this.handleScrollEvent.bind(this), 150), { passive: true });
        this.handleResizeEvent();
        window.addEventListener('resize', throttle(this.handleResizeEvent.bind(this), 150), { passive: true });
        if (this.focus) {
            this.$el.setAttribute('tabindex', '0');
            this.$el.focus();
        }
        if (this.keyboard) {
            this.$el.addEventListener('keyup', this.handleKeyboardEvent.bind(this));
        }
        if (this.initIndex != this.currentIndex) {
            await this.$nextTick();
            this.goTo(this.initIndex, 'item');
        }
        if (this.initPage != this.currentPage) {
            await this.$nextTick();
            this.goTo(this.initIndex, 'page');
        }
        this.resetAutoplayTimer();
    },
    destroy() {
        this.pauseAutoplay();
        this.$el.removeEventListener('keyup', this.handleKeyboardEvent);
        this.$el.removeEventListener('scroll', this.handleScrollEvent);
        this.$el.removeEventListener('resize', this.handleResizeEvent);
        this.$nextTick(() => {
            document.body.focus();
        });
    },
}));
const FadeSlider = createComponent((opts) => ({
    autoplay: opts.autoplay ?? false,
    autoplayInterval: opts.autoplayInterval ?? 4000,
    shuffle: opts.shuffle ?? false,
    totalSlides: opts.totalSlides ?? 0,
    activeIndex: 0,
    timer: null,
    shuffledOrder: [],
    currentIndexInShuffle: 0,
    generateShuffledOrder() {
        const indices = Array.from(Array(this.totalSlides).keys());
        for (let i = indices.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [indices[i], indices[j]] = [indices[j], indices[i]];
        }
        this.shuffledOrder = indices;
    },
    next() {
        if (this.shuffle) {
            this.currentIndexInShuffle++;
            if (this.currentIndexInShuffle >= this.totalSlides) {
                this.currentIndexInShuffle = 0;
                this.generateShuffledOrder();
            }
            this.activeIndex = this.shuffledOrder[this.currentIndexInShuffle];
        }
        else {
            this.activeIndex = (this.activeIndex + 1) % this.totalSlides;
        }
    },
    async start() {
        await this.$nextTick();
        if (this.totalSlides <= 1)
            return;
        if (this.shuffle) {
            this.generateShuffledOrder();
            this.activeIndex = this.shuffledOrder[0];
        }
        if (this.autoplay) {
            this.timer = setInterval(() => this.next(), this.autoplayInterval);
        }
    },
    destroy() {
        clearInterval(this.timer ? this.timer : undefined);
    },
}));
const VerticalSlider = createComponent((opts) => ({
    initIndex: opts.initIndex ?? 0,
    initPage: opts.initIndex ?? 0,
    keyboard: opts.keyboard ?? false,
    focus: opts.focus ?? false,
    loop: opts.loop ?? false,
    _perPage: opts.perPage ?? 1,
    pages: 0,
    scrollTop: 0,
    offsetHeight: 0,
    scrollHeight: 0,
    get currentPage() {
        return this.noNext
            ? this.pages - 1
            : Math.floor(this.currentIndex / this.itemsPerPage);
    },
    get currentIndex() {
        const scrollEl = Array.from(this.$refs.wrapper.querySelectorAll(':scope > *')).find((el) => el.offsetTop >= this.scrollTop &&
            this.scrollTop < el.offsetTop + el.offsetHeight);
        return DOM.getChildIndex(this.$refs.wrapper, scrollEl);
    },
    get itemsPerPage() {
        if (typeof this._perPage === 'number') {
            return this._perPage;
        }
        const [, perPage = 1] = Object.entries(this._perPage).findLast(([key]) => Helpers.Screen.isWiderThanBreakpoint(key)) ?? [];
        return perPage;
    },
    get totalItems() {
        return DOM.getChildCount(this.$refs.wrapper);
    },
    get isStart() {
        return this.scrollTop == 0;
    },
    get isEnd() {
        return this.scrollHeight - this.scrollTop - this.offsetHeight <= 0;
    },
    get noPrev() {
        return this.loop ? false : this.isStart;
    },
    get noNext() {
        return this.loop ? false : this.isEnd;
    },
    prev(navType = 'page') {
        let index = this.noNext ? this.currentIndex : this.currentPage;
        if (this.loop && this.isStart) {
            index = navType == 'page' ? this.pages : this.totalItems;
        }
        this.goTo(index - 2, navType, 'smooth');
    },
    next(navType = 'page') {
        let index = this.currentPage + 1;
        if (this.loop && this.isEnd) {
            index = 0;
        }
        this.goTo(index, navType, 'smooth');
    },
    scrollTo(index, navType = 'page') {
        this.goTo(index, navType, 'smooth');
    },
    goTo(index, navType = 'page', behavior = 'instant') {
        const toEl = DOM.getChildAt(this.$refs.wrapper, index * (navType == 'page' ? this.itemsPerPage : 1));
        this.$refs.wrapper?.scrollTo({
            top: toEl?.offsetTop ?? 0,
            behavior,
        });
    },
    handleKeyboardEvent(event) {
        switch (event.key) {
            case 'ArrowLeft':
                this.prev();
                break;
            case 'ArrowRight':
                this.next();
                break;
        }
    },
    handleScrollEvent() {
        const { scrollTop, offsetHeight, scrollHeight } = this.$refs.wrapper;
        this.scrollTop = Math.floor(scrollTop);
        this.offsetHeight = offsetHeight;
        this.scrollHeight = scrollHeight;
    },
    handleResizeEvent() {
        try {
            const childenCount = this.$refs.wrapper.childElementCount ?? 0;
            this.pages = Math.ceil(childenCount / this.itemsPerPage);
        }
        catch (e) {
            //
        }
    },
    async start() {
        await this.$nextTick();
        this.handleScrollEvent();
        this.$refs.wrapper.addEventListener('scroll', throttle(this.handleScrollEvent.bind(this), 150), { passive: true });
        this.handleResizeEvent();
        window.addEventListener('resize', throttle(this.handleResizeEvent.bind(this), 150), { passive: true });
        if (this.focus) {
            this.$el.setAttribute('tabindex', '0');
            this.$el.focus();
        }
        if (this.keyboard) {
            this.$el.addEventListener('keyup', this.handleKeyboardEvent.bind(this));
        }
        if (this.initIndex != this.currentIndex) {
            await this.$nextTick();
            this.goTo(this.initIndex, 'item');
        }
        if (this.initPage != this.currentPage) {
            await this.$nextTick();
            this.goTo(this.initIndex, 'page');
        }
    },
    destroy() {
        this.$el.removeEventListener('keyup', this.handleKeyboardEvent);
        this.$el.removeEventListener('scroll', this.handleScrollEvent);
        this.$el.removeEventListener('resize', this.handleResizeEvent);
        this.$nextTick(() => {
            document.body.focus();
        });
    },
}));
const Sliders = { BaseSlider, FadeSlider, VerticalSlider };
export { Sliders };
